import {
  computed,
  Injectable,
  Signal,
  signal,
  WritableSignal,
  inject,
} from '@angular/core';
import { DropdownItem } from '@shared/abstraction';
import { TransactionType as TransactionTypeEnum } from '@shared/helpers/enums/transaction-type';
import { parseTo } from '@shared/helpers/utils';
import { Client } from '@shared/models';
import { HeaderService } from '@shared/services';
import { PlainObject, QueryOptions } from 'odata-query';
import { Observable, Subject, tap } from 'rxjs';
import {
  CreateFileExportRequest,
  FileResponse,
  GstarClient,
  SecurityActionResult,
  SecurityFilterRequest,
  UpdateDispositionDateSecurityRequest,
} from 'src/app/web-api-client';
import { transactionTypeItems } from '../../admin/constants/transaction-type-items';
import { Collection } from '../../admin/models';
import { SecurityGridData } from '../models/security-grid-data';
import { SecurityService } from './security.service';

type PortfolioGrid = Partial<QueryOptions<SecurityGridData>>;

@Injectable({
  providedIn: 'root',
})
export class PortfolioService {
  private readonly headerService = inject(HeaderService);
  private readonly securityService = inject(SecurityService);
  private readonly gstarClient = inject(GstarClient);

  selectedClient: WritableSignal<Client | undefined> = signal(undefined);
  selectedTransactionType: WritableSignal<DropdownItem> = signal<DropdownItem>(
    transactionTypeItems[0]
  );
  query: WritableSignal<PortfolioGrid> = signal(
    {},
    {
      equal: (_a: PortfolioGrid, _b: PortfolioGrid) => false,
    }
  );

  isFirst: Signal<boolean> = computed(() => {
    return Object.entries(this.query()).length === 0;
  });

  securitiesByClient: Subject<Collection<SecurityGridData>> = new Subject();

  updateSecurities(): Observable<Collection<SecurityGridData>> {
    const builtQuery: PortfolioGrid = this.query();
    this.setActiveSecurities(builtQuery);
    this.evaluateTransactionType(builtQuery);
    this.evaluateSelectedClient(builtQuery);
    return this.loadSecurities(builtQuery);
  }

  loadSecurities(
    query: Partial<QueryOptions<SecurityGridData>>
  ): Observable<Collection<SecurityGridData>> {
    return this.securityService.get(query).pipe(
      tap((securities: Collection<SecurityGridData>) => {
        this.securitiesByClient.next(securities);
      })
    );
  }

  removeAcquisition(securityId: number): Observable<SecurityActionResult[]> {
    return this.gstarClient.deleteAll([securityId]);
  }

  disposeAcquisition(
    securityId: number,
    dispositionDateField: Date
  ): Observable<void> {
    return this.gstarClient.dispose(
      securityId,
      new UpdateDispositionDateSecurityRequest({
        dispositionDate: dispositionDateField,
      })
    );
  }

  exportToExcel(request: CreateFileExportRequest): Observable<FileResponse> {
    return this.gstarClient.download2(request);
  }

  exportAllToExcel(
    clientId?: number,
    request?: SecurityFilterRequest
  ): Observable<FileResponse> {
    return this.gstarClient.download3(clientId ?? 0, request);
  }

  updateQuery(query: PortfolioGrid): void {
    if (this.isFirst()) {
      this.firstQuery(query);
    } else {
      this.query.set(query);
    }

    this.updateSecurities().subscribe();
  }

  updateFilter(queryFilter: PlainObject): void {
    const currentQuery: PortfolioGrid = this.query();
    currentQuery.filter = {
      ...{ isActive: true },
      ...{ 'entity/client/isActive': true },
      ...queryFilter,
    };

    currentQuery.skip = 0;

    this.query.set(currentQuery);
    this.updateSecurities().subscribe();
  }

  updateClient(client: Client | undefined): void {
    this.selectedClient.set(client);
  }

  updateTransactionType(transactionTypeItem: DropdownItem): void {
    this.selectedTransactionType.set(transactionTypeItem);
  }

  reset(): void {
    this.query.set({});
    this.selectedTransactionType.set(transactionTypeItems[0]);
  }

  private evaluateTransactionType(query: PortfolioGrid): void {
    const transactionKey = 'transactionType/transactionTypeId';
    const filters = new Map(Object.entries(<JSONObject>query.filter ?? {}));
    const selectedTransactionType = <TransactionTypeEnum>(
      this.selectedTransactionType().value
    );

    if (selectedTransactionType === TransactionTypeEnum.All) {
      filters.delete(transactionKey);
    } else {
      filters.set(transactionKey, selectedTransactionType);
    }

    query.filter = Object.fromEntries(filters);
  }

  private evaluateSelectedClient(query: PortfolioGrid): void {
    const clientIdKey = 'entity/client/clientId';
    const activeClients = 'entity/client/isActive';
    const filters = new Map(Object.entries(<JSONObject>query.filter ?? {}));
    filters.set(activeClients, true);

    if (this.headerService.selectedClient()) {
      filters.set(clientIdKey, this.headerService.selectedClient()!.id);
    } else {
      if (filters.has(clientIdKey)) {
        filters.delete(clientIdKey);
      }
    }

    query.filter = Object.fromEntries(filters);
  }

  private setActiveSecurities(query: PortfolioGrid): void {
    const filters = new Map(Object.entries(<JSONObject>query.filter ?? {}));
    filters.set('isActive', true);

    query.filter = Object.fromEntries(filters);
  }

  private firstQuery(query: PortfolioGrid): void {
    const transactionKey = 'transactionType/transactionTypeId';
    const filters = new Map(Object.entries(<JSONObject>query.filter ?? {}));
    filters.set('isActive', true);
    filters.set('entity/client/isActive', true);

    if (filters.has(transactionKey)) {
      const sessionTransactionType = parseTo<{ in: number }>(
        filters.get(transactionKey)
      );
      const transactionType = transactionTypeItems.find(
        ({ value }: DropdownItem) => sessionTransactionType.in === value
      );
      if (transactionType) {
        this.selectedTransactionType.set(transactionType);
      }
    }

    if (this.headerService.selectedClient()) {
      this.selectedClient.set(this.headerService.selectedClient());
    }

    query.filter = Object.fromEntries(filters);
    this.query.set(query);
  }
}
